import clsx from "@/utils/clsx";
import { Button } from "@cgc/ui/button";
import { Leaf, Leaf2, Lemon } from "@cgc/ui/icons";
import { Paragraph } from "@cgc/ui/typography";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import type { JSX, PropsWithChildren } from "react";
import React, { Fragment, useCallback, useState } from "react";

type ModalProps = PropsWithChildren<{
  title?: string;
  description?: string;
  isOpen?: boolean;
  setIsOpen: (status: boolean) => void;
  onClose?: () => void;
  okButton?: React.ReactNode;
  cancelButton?: React.ReactNode;
  className?: string;
  variation: "blue" | "white";
  leafless?: boolean;
}>;

export function MAModal({
  title,
  description,
  children,
  isOpen = false,
  setIsOpen,
  onClose,
  okButton = null,
  cancelButton = null,
  className,
  variation = "white",
  leafless = false,
}: ModalProps): JSX.Element {
  const hasButtons = Boolean(okButton) || Boolean(cancelButton);

  return (
    <Transition appear as={Fragment} show={isOpen}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={() => {
          setIsOpen(false);
          onClose && onClose();
        }}
        open={isOpen}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={clsx(
                  "relative w-full max-w-xl transform overflow-hidden rounded-xl pb-6 pt-3 text-left align-middle shadow-xl transition-all",
                  {
                    "bg-cgc-blue px-16 text-white": variation === "blue",
                    "text-cgc-blue bg-white px-4": variation === "white",
                  },
                  className,
                )}
              >
                {/* Accessories for blue variation. Lemon and Leaf */}
                {variation === "blue" && !leafless && (
                  <>
                    <Leaf className="absolute -left-8 top-4 h-20 w-20" />
                    <Lemon className="absolute -left-8 -top-8 h-20 w-20 rotate-[160deg]" />
                  </>
                )}

                {variation === "white" && !leafless && (
                  <>
                    <Leaf2 className="fill-cgc-green/30 absolute -left-6 -top-20 w-32 -rotate-90" />
                  </>
                )}

                {/* TODO: add accessories for white variation */}

                <Button
                  className="absolute right-1 top-1 z-10 p-1"
                  color="unstyled"
                  onClick={() => {
                    onClose && onClose();
                    setIsOpen(false);
                  }}
                >
                  <XMarkIcon className="h-10 w-10" />
                </Button>
                <div className="relative">
                  {title ? (
                    <Dialog.Title>
                      <Paragraph
                        as="div"
                        className="relative z-10 text-center"
                        size="sub2"
                      >
                        {title}
                      </Paragraph>
                    </Dialog.Title>
                  ) : null}
                  {description ? (
                    <Dialog.Description>
                      {" "}
                      <Paragraph as="div" className="text-center">
                        {description}
                      </Paragraph>
                    </Dialog.Description>
                  ) : null}
                  {children}
                  {hasButtons ? (
                    <div className="my-2 flex flex-row justify-center gap-4">
                      {okButton}
                      {cancelButton}
                    </div>
                  ) : null}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export interface UseModalReturnType {
  Modal: React.FC<Omit<ModalProps, "setIsOpen">>;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useMAModal = (status = false): UseModalReturnType => {
  const [isOpen, setIsOpen] = useState(status);

  const MemoizedModal = useCallback(
    (props: Omit<ModalProps, "setIsOpen">) => (
      <MAModal {...props} isOpen={isOpen} setIsOpen={setIsOpen} />
    ),
    [isOpen, setIsOpen],
  );

  return {
    Modal: MemoizedModal,
    setIsOpen,
  };
};
